import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  ACCESS_KEY_QUERY_PARAM,
  API_REFRESH,
  APPLICATION_NAMES,
  AUTHENTICATION_ERROR,
  CACHED_COMPANY,
  CLIENT_ID_QUERY_PARAM,
  CONVERT_SECONDS_TO_MS,
  EPM_CLIENT_ID,
  ENCOMPASS_CLIENT_ID,
  EPM_EFAN,
  EYEFINITY_CLIENT_ID,
  LOGIN_KEY,
  OLD_EPM_CLIENT_ID,
  OLD_EYEFINITY_CLIENT_ID,
  PECONN_EXPIRATION_TIME,
  PECONN_TOKEN,
  PeConnAccessToken,
  REFRESH_TIME_ALLOWANCE,
  REFRESHER,
  ROUTES_EPM,
  ROUTES_ENCOMPASS,
  ROUTES_EYEFINITY,
} from '@share/constants';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthenticationService } from '../../data-services/authentication/authentication.service';
import { Company } from '@openapi';
import { NGXLogger } from 'ngx-logger';
import { create } from '@share/utils/create';
import { RefresherStatus } from '../../data-services/refresher-status.enum';
import { mockPeConnAccessToken } from '@share/constants-mock-database';

@Injectable({
  providedIn: 'root',
})
export class OauthService {
  broadcaster: BroadcastChannel;
  refreshTimer: number;

  constructor(
    private router: Router,
    private httpClient: HttpClient,
    private authenticationService: AuthenticationService,
    private logger: NGXLogger,
  ) {
    this.broadcaster = new BroadcastChannel(REFRESHER);
  }

  /**
   * Gets the locally stored incomingQueryParameters loadValue or returns a json object representing empty strings if
   * no current query paraemeters exist*/
  getIncomingQueryParameters(route?: ActivatedRouteSnapshot): {
    companyId: string;
    username: string;
  } {
    if (environment.envName === 'dev' && environment.mockDatabase) {
      return { companyId: '936', username: 'trapper' };
    } else if (
      route &&
      route.queryParams &&
      route.queryParams.companyId &&
      route.queryParams.user
    ) {
      return {
        companyId: route.queryParams.companyId,
        username: route.queryParams.user,
      };
    } else {
      return { companyId: null, username: null };
    }
  }

  getAccessToken(): PeConnAccessToken {
    return this.authenticationService.getPeConnAccessToken();
  }

  getSwitchedToken(): Observable<PeConnAccessToken> {
    return this.authenticationService.getSwitchedToken();
  }

  /**
   * responsible for getting the epm un and fun in the form of a
   * query parameter like &un=trapper&fun=trapper, the admin api will automatically adjust fun=trapper
   * to fun=trapper_pm_936_pm
   */
  getEpmUserCredentials(route?: ActivatedRouteSnapshot): string {
    const user = this.getIncomingQueryParameters(route).username;
    const companyId = this.getIncomingQueryParameters(route).companyId;
    if (!user || user === '' || !companyId || companyId === '') {
      return '';
    }
    const epmSignOnUrl = '&un={{un}}&fun={{fun}}';
    const unStr = user;
    const devFunStr = companyId;
    // const funStr = unStr + "_pm_" + companyId + "_pm";

    // build link via replacements
    let linkUrl = epmSignOnUrl.replace('{{un}}', unStr);

    // if (this.getEnvironment().envName === 'dev') {
    linkUrl = linkUrl.replace('{{fun}}', devFunStr);
    // } else {
    //   linkUrl = linkUrl.replace("{{fun}}", funStr);
    // }
    this.logger.debug('getEpmUserCredentials() =', linkUrl);
    return linkUrl;
  }

  /**
   * returns the client id if one is present in the url otherwise it returns an empty string;
   */
  getClientId(route?: ActivatedRouteSnapshot): string {
    let url = '?clientId=';
    if (
      route &&
      route.queryParams &&
      route.queryParams[CLIENT_ID_QUERY_PARAM]
    ) {
      const clientId = this.convertFromUsingOldClientId(
        route.queryParams[CLIENT_ID_QUERY_PARAM],
      );
      url += clientId;
      this.authenticationService.addToSessionStorage(
        CLIENT_ID_QUERY_PARAM,
        clientId,
      );
    } else if (this.router && this.router.url) {
      if (this.router.url.includes(ROUTES_EYEFINITY)) {
        url += EYEFINITY_CLIENT_ID;
        this.authenticationService.addToSessionStorage(
          CLIENT_ID_QUERY_PARAM,
          EYEFINITY_CLIENT_ID,
        );
      } else if (this.router.url.includes(ROUTES_EPM)) {
        url += EPM_CLIENT_ID;
        this.authenticationService.addToSessionStorage(
          CLIENT_ID_QUERY_PARAM,
          EPM_CLIENT_ID,
        );
      } else if (this.router.url.includes(ROUTES_ENCOMPASS)) {
        url += ENCOMPASS_CLIENT_ID;
        this.authenticationService.addToSessionStorage(
          CLIENT_ID_QUERY_PARAM,
          ENCOMPASS_CLIENT_ID,
        );
      } else {
        const clientId = this.authenticationService.getFromSessionStorage(
          CLIENT_ID_QUERY_PARAM,
        );
        if (
          clientId === EYEFINITY_CLIENT_ID ||
          clientId === EPM_CLIENT_ID ||
          clientId === ENCOMPASS_CLIENT_ID
        ) {
          url += clientId;
        } else {
          url = '';
        }
      }
    } else {
      const clientId = this.authenticationService.getFromSessionStorage(
        CLIENT_ID_QUERY_PARAM,
      );
      if (
        clientId === EYEFINITY_CLIENT_ID ||
        clientId === EPM_CLIENT_ID ||
        clientId === ENCOMPASS_CLIENT_ID
      ) {
        url += clientId;
      } else {
        url = '';
      }
    }
    return url;
  }

  /**
   * Supports converting calls from EPM or other query parameter based urls using the old client ids
   * to the new client ids.
   * @param oldClientId
   */
  convertFromUsingOldClientId(oldClientId: string): string {
    switch (oldClientId) {
      case OLD_EPM_CLIENT_ID:
        return EPM_CLIENT_ID;
      case OLD_EYEFINITY_CLIENT_ID:
        return EYEFINITY_CLIENT_ID;
      default:
        return oldClientId;
    }
  }

  messagePeConn(message: RefresherStatus) {
    this.logger.debug('Sending isLoading: ', message, ' to peconn');
    this.broadcaster.postMessage(message);
  }

  /**
   * obeservable error handler for the method.  Removes all peconn access token from local storage when an error
   * is encountered.  Also broadcasts that an error has occurred so the rest of the application is aware of the error
   * and act accordingly.
   * @param err
   */
  handleError(err: HttpErrorResponse): Observable<HttpErrorResponse> {
    if (err && err.status === AUTHENTICATION_ERROR) {
      const loginKey: string =
        this.authenticationService.getFromSessionStorage(LOGIN_KEY);
      this.logger.debug('loginKey is set to: ', loginKey);
      if (loginKey) {
        this.authenticationService.removeAllFromStorageForLogout();
        this.login(loginKey);
      } else {
        this.login();
      }
    }
    this.messagePeConn(RefresherStatus.error);
    this.logger.error(
      'An error occurred when trying to obtain a token from the server: ',
      err,
    );
    return of(err);
  }

  /**
   * Epm specific feature to look for and pull out the efan from a passed in pingfed accessToken.
   * @param route
   */
  extractEfanFromAccessToken(route?: ActivatedRouteSnapshot): void {
    if (
      route &&
      route.queryParams &&
      route.queryParams[ACCESS_KEY_QUERY_PARAM]
    ) {
      const accessTokenString = route.queryParams[ACCESS_KEY_QUERY_PARAM];
      const startOfPayload = accessTokenString.indexOf('.') + 1;
      const endOfPayload = accessTokenString.lastIndexOf('.');
      const payload = accessTokenString.substring(startOfPayload, endOfPayload);
      const decodedString = atob(payload);

      const accessTokenJSON = JSON.parse(decodedString);
      if (
        accessTokenJSON &&
        Object.prototype.hasOwnProperty.call(accessTokenJSON, 'efan')
      ) {
        this.authenticationService.addToSessionStorage(
          EPM_EFAN,
          accessTokenJSON.efan,
        );
      }
    }
  }

  /**
   * Call api attempting to get a refresh token
   */
  refreshAuthToken$(
    route?: ActivatedRouteSnapshot,
  ): Observable<HttpErrorResponse | boolean> {
    const userCred = this.getEpmUserCredentials(route) || '';
    return this.executeTokenRefresh(route, userCred);
  }

  /**
   * Call API attempting to get a refresh token for Eyefinity users with EFAN.
   * This is a specialized flow for Eyefinity clients that provides the EFAN
   * to identify the company since Eyefinity users don't have EPM credentials.
   *
   * @param route - The activated route snapshot
   * @param efan - The Eyefinity Account Number from the URL
   * @returns Observable that emits true if refresh succeeds, false otherwise
   */
  refreshAuthTokenWithEfan$(
    route?: ActivatedRouteSnapshot,
    efan?: string,
  ): Observable<HttpErrorResponse | boolean> {
    const efanParam = efan ? '&efan=' + efan : '';
    this.logger.debug(
      'OauthService: Eyefinity EFAN token refresh with EFAN:',
      efan,
    );
    return this.executeTokenRefresh(route, efanParam);
  }

  /**
   * Performs additional cleanup procedures required for Encompass users during logout.
   * - closing pe-conn tab closes
   * - redirecting the parent tab (encompass) to the encompass logout URL.
   */
  cleanUpForEncompass() {
    this.logger.debug(
      'Starting additional clean up procedure for Encompass user',
    );
    const window = this.getWindow();
    if (window.opener && !window.opener.closed) {
      window.opener.location = environment.encompassLogoutUrl;
    }
    window.self.close();
  }

  /**
   * Common engine method that executes the token refresh HTTP call.
   * This method contains the shared logic for all authentication flows.
   * VBD Pattern: This is the "engine" that performs the actual work.
   *
   * @param route - The activated route snapshot
   * @param additionalParams - Additional URL parameters (e.g., user credentials or EFAN)
   * @returns Observable that emits true if refresh succeeds, false otherwise
   */
  private executeTokenRefresh(
    route?: ActivatedRouteSnapshot,
    additionalParams = '',
  ): Observable<HttpErrorResponse | boolean> {
    // Setup: Broadcast loading status and prepare headers
    this.messagePeConn(RefresherStatus.loading);
    const headers = {
      withCredentials: true,
      observe: 'response' as const,
    };

    // Build URL components
    const clientId = this.getClientId(route);
    this.extractEfanFromAccessToken(route);
    const redirect = this.buildRedirectParameter(clientId);
    const url = this.buildRefreshUrl(clientId, redirect, additionalParams);

    // Execute: Make HTTP call (mock or real)
    return this.makeTokenRefreshHttpCall(url, headers);
  }

  /**
   * Builds the redirect URL parameter based on whether clientId exists.
   * VBD Pattern: Focused helper for URL building.
   *
   * @param clientId - The client ID string (may be empty)
   * @returns Redirect parameter string with appropriate prefix (& or ?)
   */
  private buildRedirectParameter(clientId: string): string {
    const redirectUrl = this.authenticationService.getRedirectUrl();
    return clientId && 0 < clientId.length
      ? '&redirectUrl=' + redirectUrl
      : '?redirectUrl=' + redirectUrl;
  }

  /**
   * Constructs the complete refresh token URL.
   * VBD Pattern: Focused helper for URL construction.
   *
   * @param clientId - The client ID parameter
   * @param redirect - The redirect URL parameter
   * @param additionalParams - Additional parameters (user credentials or EFAN)
   * @returns Complete URL string for token refresh
   */
  private buildRefreshUrl(
    clientId: string,
    redirect: string,
    additionalParams: string,
  ): string {
    return (
      this.getEnvironment().oauthUrl +
      API_REFRESH +
      clientId +
      redirect +
      additionalParams
    );
  }

  /**
   * Makes the actual HTTP call to refresh the token.
   * Handles both mock (dev) and real API calls.
   * VBD Pattern: Execution engine for HTTP operations.
   *
   * @param url - The complete URL to call
   * @param headers - HTTP headers configuration
   * @returns Observable with token refresh result
   */
  private makeTokenRefreshHttpCall(
    url: string,
    headers: { withCredentials: boolean; observe: 'response' },
  ): Observable<HttpErrorResponse | boolean> {
    if (
      this.getEnvironment().envName === 'dev' &&
      this.getEnvironment().mockDatabase
    ) {
      return of(mockPeConnAccessToken()).pipe(
        map((token) => this.createLocalToken(token)),
      );
    } else {
      return this.httpClient.get<PeConnAccessToken>(url, headers).pipe(
        map((token) => this.createLocalToken(token.body as PeConnAccessToken)),
        catchError((err) => this.handleError(err)),
      );
    }
  }

  /**
   * create a new cookie based on the passed in token.  Will attempt to extract the expires time from the
   * token and set up the asyncTokenTimer based on when the token will expire
   * @param peConnAccessToken - the model pe conn gets from pe-admin-api
   */
  createLocalToken(peConnAccessToken: PeConnAccessToken): boolean {
    if (!peConnAccessToken) {
      return false;
    } else if (
      peConnAccessToken.location &&
      peConnAccessToken.location.length !== 0
    ) {
      this.authenticationService.addToSessionStorage(
        LOGIN_KEY,
        peConnAccessToken.location,
      );
      this.getWindow().location.replace(peConnAccessToken.location);
      return false;
    }
    peConnAccessToken =
      this.compareAndReplaceEpmEfanToPeConnAccessToken(peConnAccessToken);
    const expirationTime =
      CONVERT_SECONDS_TO_MS *
      (peConnAccessToken.expires_in
        ? peConnAccessToken.expires_in
        : PECONN_EXPIRATION_TIME) *
      REFRESH_TIME_ALLOWANCE;
    this.authenticationService.addToSessionStorage(
      PECONN_TOKEN,
      peConnAccessToken,
    );
    this.startAsyncTokenTimer(expirationTime);
    this.messagePeConn(RefresherStatus.done);
    return true;
  }

  /**
   * Given the peconn access token this will compare the EPM_EFAN we should have in local storage to the efan we have
   * in the access token.  If we have a string "null" in the access token then we'll replace it with the efan we have
   * in local storage.  Otherwise no action should be taken
   * @param peConnAccessToken
   */
  compareAndReplaceEpmEfanToPeConnAccessToken(
    peConnAccessToken: PeConnAccessToken,
  ): PeConnAccessToken {
    const isThisTokenForEpm =
      peConnAccessToken &&
      (peConnAccessToken.application === APPLICATION_NAMES.epm ||
        peConnAccessToken.application === APPLICATION_NAMES.pe ||
        peConnAccessToken.application === APPLICATION_NAMES.encompass);
    const isThisTokensEfanNull =
      (peConnAccessToken && peConnAccessToken.efan === 'null') ||
      !peConnAccessToken.efan;
    const isThisTokensCompanyIdNull =
      peConnAccessToken?.companyId === null || !peConnAccessToken?.companyId;
    const efan = this.getEpmEfan();
    const companyId = this.getEpmCompanyId();
    if (isThisTokenForEpm && isThisTokensEfanNull && efan) {
      if (efan.toString().length === 10) {
        peConnAccessToken.efan = efan.toString();
      }
    }
    if (isThisTokenForEpm && isThisTokensCompanyIdNull && companyId) {
      peConnAccessToken.companyId = companyId;
    }
    return peConnAccessToken;
  }

  /**
   * Removes the tokens necessary from session storage to make pe-conn appear
   * as though it's no longer logged in as a user.
   */
  removeAllFromStorage() {
    this.authenticationService.removeAllFromStorageForLogout();
  }

  /**
   * Attempt to use the stored login url saved during the initial login process.  If for some reason
   * no login url is found attempt to default to the redirect url.
   */
  login(overrideLocation?: string) {
    const url: string = overrideLocation
      ? overrideLocation
      : this.authenticationService.getFromSessionStorage(LOGIN_KEY);
    if (url) {
      this.getWindow().location.replace(url);
    } else {
      this.getWindow().location.replace(
        this.authenticationService.getRedirectUrl(),
      );
    }
  }

  /**
   * starts an async timer to call refreshAuthToken$ when the expiresInTimer is up.  This should get set one and run
   * until the app is closed.  If a refresh timer already exists, then don't do anything.
   * @param expiresInTimer
   */
  startAsyncTokenTimer(expiresInTimer: number): void {
    if (!this.refreshTimer) {
      this.logger.debug(
        'Starting async token refresh timer with token expiration in ',
        expiresInTimer,
      );
      this.refreshTimer = this.getWindow().setInterval(
        () =>
          this.refreshAuthToken$().subscribe(
            (result) => {
              this.logger.debug('Result of token refresh: ', result);
            },
            (err) => {
              this.logger.error(
                'An error occurred while trying to refresh the current token: ',
                err,
              );
            },
          ),
        expiresInTimer,
      );
    } else {
      this.logger.debug('refresh timer already exists: ', this.refreshTimer);
    }
  }

  /**
   * returns the current EPM_EFAN from local storage or returns null;
   */
  getEpmEfan(): string {
    const efan = this.authenticationService.getFromSessionStorage(EPM_EFAN);
    return efan ? efan.toString() : null;
  }

  getEpmCompanyId(): string {
    const co = this.authenticationService.getFromSessionStorage(
      CACHED_COMPANY,
      () => create<Company>(),
    ) as Company;
    return co?.epmCompanyId ? co.epmCompanyId : null;
  }

  /**
   * function allows us to more easily mock the window object in testing
   */
  getWindow(): Window {
    return window;
  }

  /**
   * function allows us to more easily mock the environment in testing.
   */
  getEnvironment() {
    return environment;
  }
}
